import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Define types for token response
interface TokenResponse {
  access: string;
  refresh: string;
}

// Create an Axios instance
const apiClient = axios.create({
  baseURL: BASE_URL, // Replace with your API base URL
});

// Function to get access token from local storage (or another source)
const getAccessToken = (): string | null =>
  localStorage.getItem("access_token");

// Function to get refresh token from local storage (or another source)
const getRefreshToken = (): string | null =>
  localStorage.getItem("refresh_token");

// Function to save new tokens to local storage
const saveTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem("access_token", accessToken);
  localStorage.setItem("refresh_token", refreshToken);
};

// Function to clear tokens and redirect to login page
const handleLogout = (): void => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  window.location.href = "/login"; // Redirect to the login page
};

// Add a request interceptor to attach the access token to headers
apiClient.interceptors.request.use(
  //@ts-ignore
  (config: AxiosRequestConfig): AxiosRequestConfig => {
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers = {
        ...config.headers,
        "Accept-Language": "fa",
        Authorization: `Bearer ${accessToken}`,
      };
    }
    return config;
  },
  (error: AxiosError): Promise<AxiosError> => Promise.reject(error)
);

// Add a response interceptor to handle token expiration
apiClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    // Return response if it’s successful (status code 2xx)
    return response;
  },
  async (error: AxiosError): Promise<AxiosError> => {
    const originalRequest = error.config;

    // Check if the error is because of expired token (status code 401)
    if (
      error.response &&
      error.response.status === 401 &&
      originalRequest &&
      //@ts-ignore
      !originalRequest._retry
    ) {
      //@ts-ignore
      originalRequest._retry = true; // Avoid infinite loops

      const refreshToken = getRefreshToken();

      if (refreshToken && refreshToken !== "undefined") {
        try {
          // Set a flag to prevent multiple refresh attempts
          //@ts-ignore
          originalRequest._retry = true;

          const response = await axios.post<TokenResponse>(
            `${BASE_URL}/token/refresh`,
            {
              refresh: refreshToken,
            }
          );

          const { access: newAccessToken, refresh: newRefreshToken } =
            response.data;

          // Save the new tokens
          saveTokens(newAccessToken, newRefreshToken);

          // Update the Authorization header with the new access token
          apiClient.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${newAccessToken}`;
          //@ts-ignore
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

          // Retry the original request with the new access token
          return apiClient(originalRequest);
        } catch (refreshError) {
          // If refresh token request fails, clear tokens and redirect to login
          handleLogout();
          return Promise.reject(refreshError);
        }
      } else {
        // If there's no refresh token, log out the user
        handleLogout();
      }
    }

    // Reject any other errors
    return Promise.reject(error);
  }
);

// Define helper functions for CRUD operations

// GET request
export const getRequest = <T>(
  url: string,
  config: AxiosRequestConfig = {}
): Promise<AxiosResponse<T>> => {
  return apiClient.get<T>(url, config);
};

// POST request
export const postRequest = <T>(
  url: string,
  data: any,
  config: AxiosRequestConfig = {}
): Promise<AxiosResponse<T>> => {
  return apiClient.post<T>(url, data, config);
};

// PUT request (for updating resources)
export const putRequest = <T>(
  url: string,
  data: any,
  config: AxiosRequestConfig = {}
): Promise<AxiosResponse<T>> => {
  return apiClient.put<T>(url, data, config);
};

// PATCH request (for partially updating resources)
export const patchRequest = <T>(
  url: string,
  data: any,
  config: AxiosRequestConfig = {}
): Promise<AxiosResponse<T>> => {
  return apiClient.patch<T>(url, data, config);
};

// DELETE request (for deleting resources)
export const deleteRequest = <T>(
  url: string,
  config: AxiosRequestConfig = {}
): Promise<AxiosResponse<T>> => {
  return apiClient.delete<T>(url, config);
};

export default apiClient;
